// GlobalStore.tsx
import { useState, useEffect } from 'react';
import { debounce } from 'lodash';
import { siteConfig } from '../config/siteConfig';

type Subscriber<T> = (value: T) => void;

export interface StoryFlyTo {
  geoid: string;
  ts: number; // timestamp to de-dup
}

export interface StoryFitBounds {
  ts: number; // timestamp to de-dup
}

class GlobalStore {
  // INSTANTIATE VARIABLES
  private swarmCalibration:any = {};
  private swarmCalibrationSubscribers: Subscriber<any>[] = [];

  private storyFlyTo: StoryFlyTo | null = null;
  private storyFlyToSubscribers: Subscriber<StoryFlyTo | null>[] = [];

  private storyFitBounds: StoryFitBounds | null = null;
  private storyFitBoundsSubscribers: Subscriber<StoryFitBounds | null>[] = [];

  // DEFINE METHODS
  setSwarmCalibration = (config: any) => {
    this.swarmCalibration = config;
    this.swarmCalibrationSubscribers.forEach(sub => sub(this.swarmCalibration));
  };

  setStoryFlyTo = (value: StoryFlyTo | null) => {
    this.storyFlyTo = value;
    this.storyFlyToSubscribers.forEach(sub => sub(this.storyFlyTo));
  };

  setStoryFitBounds = (value: StoryFitBounds | null) => {
    this.storyFitBounds = value;
    this.storyFitBoundsSubscribers.forEach(sub => sub(this.storyFitBounds));
  };

  // SUBSCRIBE TO CHANGES
  subscribeToSwarmCalibration = (subscriber: Subscriber<any>) => {
    this.swarmCalibrationSubscribers.push(subscriber);
    return () => {
      this.swarmCalibrationSubscribers = this.swarmCalibrationSubscribers.filter(sub => sub !== subscriber);
    };
  };

  subscribeToStoryFlyTo = (subscriber: Subscriber<StoryFlyTo | null>) => {
    this.storyFlyToSubscribers.push(subscriber);
    return () => {
      this.storyFlyToSubscribers = this.storyFlyToSubscribers.filter(sub => sub !== subscriber);
    };
  };

  subscribeToStoryFitBounds = (subscriber: Subscriber<StoryFitBounds | null>) => {
    this.storyFitBoundsSubscribers.push(subscriber);
    return () => {
      this.storyFitBoundsSubscribers = this.storyFitBoundsSubscribers.filter(sub => sub !== subscriber);
    };
  };

  // GETTERS
  getSetSwarmCalibration = () => (config: any) => {
    this.setSwarmCalibration(config);
  };

  getSetStoryFlyTo = () => (value: StoryFlyTo | null) => {
    this.setStoryFlyTo(value);
  };

  getSetStoryFitBounds = () => (value: StoryFitBounds | null) => {
    this.setStoryFitBounds(value);
  };
}

// INSTANTIATE THE STORE
const globalStore = new GlobalStore();

// EXPORT THE STORE
export const useGlobalSwarmCalibration = () => {
  const [swarmCalibration, setSwarmCalibration] = useState<any>(siteConfig.swarmCalibration);
  useEffect(() => {
    const unsubscribe = globalStore.subscribeToSwarmCalibration(setSwarmCalibration);
    return unsubscribe; // Correctly returning the unsubscribe function
  }, []);

  return swarmCalibration;
};

export const useGlobalStoryFlyTo = () => {
  const [storyFlyTo, setStoryFlyTo] = useState<StoryFlyTo | null>(null);
  useEffect(() => {
    const unsubscribe = globalStore.subscribeToStoryFlyTo(setStoryFlyTo);
    return unsubscribe;
  }, []);
  return storyFlyTo;
};

export const useGlobalStoryFitBounds = () => {
  const [storyFitBounds, setStoryFitBounds] = useState<StoryFitBounds | null>(null);
  useEffect(() => {
    const unsubscribe = globalStore.subscribeToStoryFitBounds(setStoryFitBounds);
    return unsubscribe;
  }, []);
  return storyFitBounds;
};

// EXPORT THE STORE METHODS
export const onSetSwarmCalibration = globalStore.getSetSwarmCalibration();
export const onSetStoryFlyTo = globalStore.getSetStoryFlyTo();
export const onSetStoryFitBounds = globalStore.getSetStoryFitBounds();
